import { useEffect, useState } from "react";
import { useParams, Link, useSearchParams, useNavigate } from "react-router-dom";
import PagamentoCard from "@/components/PagamentoCard";
import { api, money, formatDate, fileUrl, publicPdfUrl, publicQuoteUrl, cadastroClienteUrl, buildSistemaWhatsappMessage, openWhatsapp } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { useClienteAuth } from "@/lib/clienteAuth";
import SignaturePad from "@/components/SignaturePad";
import {
  CheckCircle2, XCircle, MessageCircle, Eye, FileDown, RotateCcw, Send, Pencil, ThumbsUp, ThumbsDown, Copy, UserPlus, CreditCard, DollarSign, ArrowLeft,
} from "lucide-react";

const formatPhoneBR = (v) => {
  const d = (v || "").replace(/\D/g, "").replace(/^55/, "");
  if (!d) return "";
  const dd = d.slice(0, 2);
  const rest = d.slice(2);
  if (rest.length === 9) return `(${dd}) ${rest.slice(0, 5)}-${rest.slice(5)}`;
  if (rest.length === 8) return `(${dd}) ${rest.slice(0, 4)}-${rest.slice(4)}`;
  return `(${dd}) ${rest}`;
};

export default function PublicOrcamento() {
  const { token } = useParams();
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const { ctx: clienteCtx } = useClienteAuth();
  const clienteLogado = !!clienteCtx;
  const [sp] = useSearchParams();
  const fromAdmin = isAdmin && sp.get("admin") === "1";
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDuvida, setShowDuvida] = useState(false);
  const [showReabrir, setShowReabrir] = useState(false);
  const [showEnviarCliente, setShowEnviarCliente] = useState(false);
  const [showAprovar, setShowAprovar] = useState(false);
  const [showRecusar, setShowRecusar] = useState(false);
  const [duvida, setDuvida] = useState("");
  const [motivoReab, setMotivoReab] = useState("");
  const [motivoRecusa, setMotivoRecusa] = useState("");
  const [enviando, setEnviando] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/public/orcamento/${token}`);
      setData(data);
    } catch {
      setData({ notFound: true });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (loading)
    return <div className="min-h-screen flex items-center justify-center text-zinc-500">Carregando orçamento...</div>;

  if (data?.notFound)
    return (
      <div className="min-h-screen flex items-center justify-center px-6 text-center">
        <div>
          <div className="font-heading text-2xl font-bold mb-2">Orçamento não encontrado</div>
          <p className="text-zinc-500">Verifique o link enviado.</p>
        </div>
      </div>
    );

  const { orcamento: o, cliente, empresa } = data;
  const isFinalizado = ["aprovado", "recusado", "expirado", "finalizado", "pago"].includes(o.status);
  const podeAgir = !isFinalizado && !isAdmin;
  const pedidoReab = o.pedido_reabertura;

  const path = (u) => (u || "").replace("/api/files/", "");

  // === Client: aprovar orçamento (com assinatura) ===
  const aprovar = async (dataUrl) => {
    setEnviando(true);
    try {
      await api.post(`/public/orcamento/${token}/aprovar`, { assinatura: dataUrl });
      toast.success("Orçamento aprovado! A BBH foi notificada.");
      setShowAprovar(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Não foi possível aprovar");
    } finally {
      setEnviando(false);
    }
  };

  // === Client: recusar orçamento ===
  const recusar = async () => {
    setEnviando(true);
    try {
      await api.post(`/public/orcamento/${token}/recusar`, {
        motivo: motivoRecusa.trim() || null,
        observacao: motivoRecusa.trim() || null,
      });
      toast.success("Recusa registrada. A BBH foi notificada.");
      setShowRecusar(false);
      setMotivoRecusa("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Não foi possível recusar");
    } finally {
      setEnviando(false);
    }
  };

  // === Client: enviar dúvida/pedido à BBH ===
  const enviarDuvida = async () => {
    setEnviando(true);
    try {
      if (duvida.trim()) {
        await api.post(`/public/orcamento/${token}/observacao`, { observacao: duvida.trim() });
      }
    } catch {
      /* segue */
    } finally {
      setEnviando(false);
    }
    const numero = (empresa.whatsapp || empresa.telefone || "").replace(/\D/g, "");
    const linhas = [
      `Olá, aqui é ${cliente?.nome || "o cliente"}.`,
      "",
      `Tenho uma dúvida sobre o orçamento nº ${o.numero_orcamento}.`,
    ];
    if (duvida.trim()) linhas.push("", "Observação:", duvida.trim());
    linhas.push("", "Link do orçamento:", publicQuoteUrl(token));
    const msg = encodeURIComponent(linhas.join("\n"));
    window.open(numero ? `https://wa.me/${numero}?text=${msg}` : `https://wa.me/?text=${msg}`, "_blank");
    setShowDuvida(false);
    setDuvida("");
    if (duvida.trim()) toast.success("Sua mensagem foi registrada");
    load();
  };

  // === Client: solicitar reabertura ===
  const solicitarReabertura = async () => {
    setEnviando(true);
    try {
      await api.post(`/public/orcamento/${token}/reabertura`, {
        motivo: motivoReab.trim() || null,
        observacao: motivoReab.trim() || null,
      });
      toast.success("Pedido de reabertura enviado à BBH");
      // Notifica também via WhatsApp
      const numero = (empresa.whatsapp || empresa.telefone || "").replace(/\D/g, "");
      const linhas = [
        `Olá, aqui é ${cliente?.nome || "o cliente"}.`,
        "",
        `Gostaria de solicitar a reabertura/reavaliação do orçamento nº ${o.numero_orcamento} (atualmente ${o.status}).`,
      ];
      if (motivoReab.trim()) linhas.push("", "Motivo:", motivoReab.trim());
      linhas.push("", "Link do orçamento:", publicQuoteUrl(token));
      const msg = encodeURIComponent(linhas.join("\n"));
      window.open(numero ? `https://wa.me/${numero}?text=${msg}` : `https://wa.me/?text=${msg}`, "_blank");
      setShowReabrir(false);
      setMotivoReab("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao solicitar reabertura");
    } finally {
      setEnviando(false);
    }
  };

  // === Admin: enviar link do orçamento ao cliente (só orçamento) ===
  const enviarParaCliente = () => {
    const nomeC = (cliente?.nome || "cliente").trim();
    const msg = buildSistemaWhatsappMessage([
      `Olá ${nomeC}, você está recebendo o orçamento nº ${o.numero_orcamento} após solicitação.`,
      "",
      "Link do orçamento:",
      publicQuoteUrl(token),
    ]);
    openWhatsapp(cliente?.whatsapp || cliente?.telefone, msg);
    setShowEnviarCliente(false);
    toast.success("WhatsApp aberto com o link do orçamento.");
  };

  // === Admin: copiar link do orçamento (só orçamento) ===
  const adminCopiarLink = () => {
    navigator.clipboard.writeText(publicQuoteUrl(token));
    toast.success("Link copiado");
  };

  // === Admin: copiar link de cadastro (cadastro + orçamento) ===
  const adminCopiarLinkCadastro = () => {
    if (!cliente?.codigo_cadastro) return toast.error("Cliente sem código de cadastro");
    const partes = [
      `Link do orçamento nº ${o.numero_orcamento}: ${publicQuoteUrl(token)}`,
      `Link para cadastro: ${cadastroClienteUrl(cliente.codigo_cadastro)}`,
    ];
    navigator.clipboard.writeText(partes.join("\n"));
    toast.success("Links copiados (orçamento + cadastro)");
  };

  // === Admin: enviar cadastro por WhatsApp (orçamento + cadastro) ===
  const adminEnviarCadastroWhatsapp = () => {
    if (!cliente?.codigo_cadastro) return toast.error("Cliente sem código de cadastro");
    const nomeC = (cliente?.nome || "cliente").trim();
    const msg = buildSistemaWhatsappMessage([
      `Olá ${nomeC}, você está recebendo o orçamento nº ${o.numero_orcamento} após solicitação.`,
      "",
      "Link do orçamento:",
      publicQuoteUrl(token),
      "",
      "Link para cadastro no portal:",
      cadastroClienteUrl(cliente.codigo_cadastro),
    ]);
    openWhatsapp(cliente?.whatsapp || cliente?.telefone, msg);
  };

  return (
    <div className="min-h-screen bbh-public bg-[#FAFAFA] pb-24" data-testid="public-page">
      {/* Admin back bar */}
      {fromAdmin && (
        <div
          className="sticky top-0 z-30 bg-[#14243D] text-white px-4 py-2 flex items-center justify-between"
          style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 0.5rem)" }}
          data-testid="admin-back-bar"
        >
          <button
            onClick={() => navigate(-1)}
            data-testid="admin-voltar-btn"
            className="inline-flex items-center gap-1.5 text-sm font-medium"
          >
            <ArrowLeft className="w-4 h-4" /> Voltar
          </button>
          <span className="text-[11px] uppercase tracking-widest opacity-80">Visualização admin</span>
          <Link
            to="/admin/orcamentos"
            data-testid="admin-ir-lista-btn"
            className="text-xs underline opacity-90"
          >
            Lista
          </Link>
        </div>
      )}
      {/* Header */}
      <header className="bg-white border-b border-zinc-200 px-5 pb-5" style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 1.25rem)" }}>
        <div className="max-w-3xl mx-auto flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            {empresa.logo ? (
              <img src={fileUrl(path(empresa.logo))} alt="" className="w-14 h-14 object-contain" />
            ) : (
              <div className="w-14 h-14 rounded-md bg-[#14243D] text-white flex items-center justify-center font-heading font-bold text-xl">B</div>
            )}
            <div>
              <div className="font-heading font-bold text-lg leading-tight">{empresa.nome_empresa || "BBH"}</div>
              <div className="text-xs text-zinc-500">{empresa.cnpj}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isAdmin && (
              <Link
                to={`/orcamentos`}
                className="hidden sm:inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-zinc-900 text-white text-xs font-semibold"
                data-testid="admin-voltar-admin"
              >
                <Pencil className="w-3 h-3" /> Painel admin
              </Link>
            )}
            <div className={`status-pill ${o.pagamento_status === "pago" ? "status-quitado" : `status-${o.status}`}`}>
              {o.pagamento_status === "pago" ? "quitado" : o.status}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 py-6 space-y-6">
        {/* Convite ao portal do cliente (apenas se ainda não cadastrado e não logado no portal) */}
        {!isAdmin && !clienteLogado && cliente?.codigo_cadastro && !cliente?.ja_cadastrado && (
          <Link
            to={`/cadastro/${cliente.codigo_cadastro}`}
            data-testid="cta-criar-conta"
            className="block bbh-card p-4 sm:p-5 border-l-4 hover:shadow-md transition-shadow"
            style={{ borderLeftColor: "var(--bbh-yellow)" }}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
                   style={{ background: "var(--bbh-yellow)" }}>
                <Send className="w-5 h-5 text-[#14243D]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-heading font-bold text-[#14243D]">Crie sua conta e acompanhe seus orçamentos</div>
                <div className="text-xs text-zinc-600 mt-0.5">
                  Cadastre-se em 30 segundos para ver este e outros orçamentos num só lugar.
                </div>
              </div>
              <span className="text-xs font-semibold text-[#14243D] hidden sm:inline">Cadastrar →</span>
            </div>
          </Link>
        )}
        {!isAdmin && !clienteLogado && cliente?.codigo_cadastro && cliente?.ja_cadastrado && (
          <Link
            to="/portal/login"
            data-testid="cta-entrar-portal"
            className="block text-center bbh-card px-4 py-3 text-xs sm:text-sm text-zinc-700 hover:bg-zinc-50"
          >
            Já tem cadastro? <span className="font-semibold text-[#14243D] underline">Entrar no portal</span>
          </Link>
        )}
        {clienteLogado && !isAdmin && (
          <Link
            to="/portal"
            data-testid="voltar-portal-link"
            className="block text-center bbh-card px-4 py-2 text-xs text-zinc-500 hover:bg-zinc-50"
          >
            ← Voltar aos meus orçamentos
          </Link>
        )}

        {/* Título orçamento */}
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-widest text-zinc-500 mb-1">Orçamento</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight" data-testid="public-numero">
            Nº {o.numero_orcamento}
          </h1>
          <div className="text-sm text-zinc-500 mt-1">{formatDate(o.criado_em)}</div>
        </div>

        {/* Cliente */}
        <section className="bbh-card p-5">
          <div className="text-[11px] font-semibold uppercase tracking-widest text-zinc-500 mb-3">Para</div>
          <div className="text-sm space-y-1">
            <div className="font-heading font-semibold text-lg">{cliente.nome}</div>
            {cliente.endereco && <div className="text-zinc-600">{cliente.endereco}</div>}
            {cliente.whatsapp && <div className="text-zinc-600">{formatPhoneBR(cliente.whatsapp)}</div>}
          </div>
        </section>

        {/* Total destaque */}
        <section className="bbh-card p-6 text-center border-[#14243D] border-2">
          <div className="text-[11px] font-semibold uppercase tracking-widest text-zinc-500">Valor total</div>
          <div className="font-heading text-5xl sm:text-6xl font-bold bbh-accent mt-2" data-testid="public-total">
            {money(o.valor_total)}
          </div>
        </section>

        {/* Itens */}
        <section>
          <div className="text-[11px] font-semibold uppercase tracking-widest text-zinc-500 mb-3">
            Serviços do orçamento
          </div>
          <div className="space-y-3">
            {o.itens.map((it, idx) => (
              <div key={it.id} className="bbh-card p-4" data-testid={`public-item-${idx}`}>
                <div className="flex items-start gap-3">
                  {it.fotos?.[0] && (
                    <img src={fileUrl(it.fotos[0].path)} alt="" className="w-24 h-24 rounded-md object-cover border border-zinc-200 shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium leading-snug whitespace-pre-wrap">{it.descricao}</div>
                    {Number(it.quantidade) > 1 && (
                      <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-0.5 text-sm">
                        <div className="text-zinc-500">Qnd</div>
                        <div className="text-right text-zinc-800 font-medium">
                          {Number(it.quantidade).toLocaleString("pt-BR")}
                        </div>
                        <div className="text-zinc-500">Valor unitário</div>
                        <div className="text-right text-zinc-800 font-medium">
                          {money(it.valor_unitario)}
                        </div>
                      </div>
                    )}
                    {it.observacao && <div className="text-xs text-zinc-500 mt-2">{it.observacao}</div>}
                    <div className="mt-2 flex items-baseline justify-between gap-3">
                      <span className="text-[11px] font-semibold uppercase tracking-widest text-zinc-500">Valor total</span>
                      <span className="font-heading font-bold">{money(it.valor_total)}</span>
                    </div>
                  </div>
                </div>
                {it.fotos?.length > 1 && (
                  <div className="mt-3 flex gap-2 overflow-x-auto">
                    {it.fotos.slice(1).map((f) => (
                      <img key={f.id} src={fileUrl(f.path)} alt="" className="w-20 h-20 rounded-md object-cover border border-zinc-200 flex-shrink-0" />
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Condições */}
        <section className="bbh-card p-5">
          <div className="text-[11px] font-semibold uppercase tracking-widest text-zinc-500 mb-3">Condições comerciais</div>
          <dl className="space-y-2 text-sm">
            <Cond k="Forma de pagamento" v={o.forma_pagamento} />
            {o.prazo_pagamento && <Cond k="Prazo de pagamento" v={o.prazo_pagamento} />}
            <Cond k="Validade da proposta" v={o.validade_proposta} />
            <Cond k="Prazo de execução" v={o.prazo_execucao} />
            <Cond k="Garantia" v={o.garantia} />
          </dl>
          <div
            className="mt-4 rounded-md border border-[#14243D]/15 bg-[#14243D]/5 p-3 text-xs text-[#14243D]/80 leading-relaxed"
            data-testid="aviso-pagamento-online"
          >
            <strong>Pagamento on-line disponível</strong> após a finalização de cada item ou do valor total ao final, conforme combinado com a BBH, via <strong>PIX</strong>. Pagamento por cartão em implantação.
          </div>
        </section>

        {o.observacao_geral && (
          <section className="bbh-card p-5">
            <div className="text-[11px] font-semibold uppercase tracking-widest text-zinc-500 mb-2">Observações</div>
            <p className="text-sm whitespace-pre-wrap">{o.observacao_geral}</p>
          </section>
        )}

        {/* Assinatura */}
        <section className="pt-2">
          {empresa.assinatura && (
            <img src={fileUrl(path(empresa.assinatura))} alt="" className="max-h-20 mb-2" />
          )}
          <div className="border-t border-zinc-900 pt-2 max-w-xs">
            <div className="font-semibold">{empresa.nome_assinante}</div>
            <div className="text-xs text-zinc-500">{empresa.cargo}</div>
          </div>
        </section>

        {/* Status finalizado */}
        {o.status === "aprovado" && o.data_aprovacao && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-md p-4 text-emerald-800 text-sm" data-testid="aprovado-feedback">
            <CheckCircle2 className="w-4 h-4 inline mr-1" /> Orçamento aprovado em {formatDate(o.data_aprovacao)}.
          </div>
        )}

        {/* Pagamento */}
        <PagamentoCard orc={o} token={token} isAdmin={isAdmin} />
        {o.status === "recusado" && (
          <div className="bg-rose-50 border border-rose-200 rounded-md p-4 text-rose-800 text-sm" data-testid="recusado-feedback">
            <XCircle className="w-4 h-4 inline mr-1" /> Orçamento recusado em {formatDate(o.data_recusa)}.
            {o.motivo_recusa && <div className="mt-1 text-xs">Motivo: {o.motivo_recusa}</div>}
          </div>
        )}
        {pedidoReab && (
          <div className="bg-amber-50 border border-amber-200 rounded-md p-4 text-amber-800 text-sm" data-testid="pedido-reabertura-badge">
            <RotateCcw className="w-4 h-4 inline mr-1" /> Pedido de reabertura enviado em {formatDate(pedidoReab.solicitada_em)}.
            {pedidoReab.motivo && <div className="mt-1 text-xs">Motivo: {pedidoReab.motivo}</div>}
          </div>
        )}

        {/* PDF actions inline */}
        <div className="flex gap-2 flex-wrap">
          <a href={publicPdfUrl(token)} target="_blank" rel="noreferrer" data-testid="public-view-pdf"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-white border border-zinc-200 text-sm font-medium hover:bg-zinc-50">
            <Eye className="w-4 h-4" /> Visualizar PDF
          </a>
          <a href={publicPdfUrl(token, true)} data-testid="public-download-pdf"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-white border border-zinc-200 text-sm font-medium hover:bg-zinc-50">
            <FileDown className="w-4 h-4" /> Baixar PDF
          </a>
        </div>
      </main>

      {/* Sticky action bar — só aparece se admin OU cliente com orçamento não finalizado */}
      {(isAdmin || !isFinalizado) && (
        <div className="fixed bottom-0 left-0 right-0 bbh-sticky-actions p-2 sm:p-3 z-40" data-testid="public-actions-bar">
          <div className="max-w-3xl mx-auto flex flex-row gap-1.5 sm:gap-2">
            {isAdmin ? (
              <>
                <button
                  onClick={adminCopiarLink}
                  data-testid="admin-copiar-link-btn"
                  title="Copiar link do orçamento (com cadastro se aplicável)"
                  className="flex-1 py-2.5 rounded-md bg-white border border-zinc-300 hover:bg-zinc-50 text-zinc-800 font-semibold flex items-center justify-center gap-1 text-xs sm:text-sm"
                >
                  <Copy className="w-4 h-4" /> <span className="hidden sm:inline">Copiar link</span>
                </button>
                <button
                  onClick={() => setShowEnviarCliente(true)}
                  data-testid="admin-enviar-cliente-btn"
                  className="flex-1 py-2.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white font-semibold flex items-center justify-center gap-1 text-xs sm:text-sm"
                >
                  <Send className="w-4 h-4" /> <span>Enviar orçamento</span>
                </button>
                {cliente?.codigo_cadastro && !cliente?.ja_cadastrado && (
                  <>
                    <button
                      onClick={adminCopiarLinkCadastro}
                      data-testid="admin-copiar-cadastro-btn"
                      title="Copiar link de cadastro"
                      className="shrink-0 px-2.5 py-2.5 rounded-md bg-white border border-zinc-300 hover:bg-zinc-50 text-zinc-800 font-semibold flex items-center justify-center gap-1 text-xs"
                    >
                      <UserPlus className="w-4 h-4" />
                    </button>
                    <button
                      onClick={adminEnviarCadastroWhatsapp}
                      data-testid="admin-wpp-cadastro-btn"
                      title="Enviar link de cadastro pelo WhatsApp"
                      className="shrink-0 px-2.5 py-2.5 rounded-md bg-[#14243D] hover:bg-[#0f1a2e] text-white font-semibold flex items-center justify-center gap-1 text-xs"
                    >
                      <MessageCircle className="w-4 h-4" />
                    </button>
                  </>
                )}
              </>
            ) : (
              <>
                <button
                  onClick={() => setShowAprovar(true)}
                  data-testid="public-aprovar-btn"
                  className="flex-1 py-2.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white font-semibold flex items-center justify-center gap-1 text-sm sm:text-base"
                >
                  <ThumbsUp className="w-4 h-4" /> Aprovar
                </button>
                <button
                  onClick={() => setShowRecusar(true)}
                  data-testid="public-recusar-btn"
                  className="flex-1 py-2.5 rounded-md bg-rose-500 hover:bg-rose-600 text-white font-semibold flex items-center justify-center gap-1 text-sm sm:text-base"
                >
                  <ThumbsDown className="w-4 h-4" /> Recusar
                </button>
                <button
                  onClick={() => setShowDuvida(true)}
                  data-testid="public-whatsapp"
                  aria-label="Enviar dúvida à BBH"
                  className="shrink-0 px-3 py-2.5 rounded-md bg-white border border-zinc-300 hover:bg-zinc-50 text-zinc-700 font-semibold flex items-center justify-center gap-1 text-sm"
                >
                  <MessageCircle className="w-4 h-4" /> <span className="hidden sm:inline">Dúvida</span>
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Ação de reabertura (recusado) — fora do sticky, aparece somente para cliente */}
      {!isAdmin && o.status === "recusado" && (
        <div className="fixed bottom-0 left-0 right-0 bbh-sticky-actions p-2 sm:p-3 z-40" data-testid="public-reabrir-bar">
          <div className="max-w-3xl mx-auto">
            <button
              onClick={() => setShowReabrir(true)}
              data-testid="public-reabrir-btn"
              disabled={!!pedidoReab}
              className="w-full py-2.5 rounded-md bg-amber-500 hover:bg-amber-600 disabled:opacity-60 text-white font-semibold flex items-center justify-center gap-2 text-sm sm:text-base"
            >
              <RotateCcw className="w-4 h-4" />
              {pedidoReab ? "Reabertura já solicitada" : "Solicitar reavaliação"}
            </button>
          </div>
        </div>
      )}

      {/* Modal dúvida (cliente) */}
      {showDuvida && (
        <Modal onClose={() => setShowDuvida(false)} title="Enviar dúvida à BBH">
          <p className="text-sm text-zinc-600 mb-1">
            Escreva sua dúvida ou observação sobre o orçamento nº <b>{o.numero_orcamento}</b>:
          </p>
          <p className="text-xs text-zinc-400 mb-3">
            Sua mensagem será registrada neste orçamento e enviada por WhatsApp à equipe BBH.
          </p>
          <textarea
            rows={5}
            value={duvida}
            onChange={(e) => setDuvida(e.target.value)}
            data-testid="duvida-textarea"
            placeholder="Ex.: Gostaria de saber se é possível trocar o item 2 por..."
            className="w-full px-3 py-2 rounded-md border border-zinc-200 bbh-input text-sm mb-3"
          />
          <div className="flex gap-2">
            <button onClick={() => setShowDuvida(false)} className="flex-1 py-3 rounded-md border border-zinc-200 font-medium">
              Cancelar
            </button>
            <button
              onClick={enviarDuvida}
              disabled={enviando}
              data-testid="enviar-duvida-btn"
              className="flex-1 py-3 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white font-semibold disabled:opacity-60 inline-flex items-center justify-center gap-2"
            >
              <MessageCircle className="w-4 h-4" /> {enviando ? "Enviando..." : "Enviar pelo WhatsApp"}
            </button>
          </div>
        </Modal>
      )}

      {/* Modal aprovar (cliente) — com assinatura digital */}
      {showAprovar && (
        <Modal onClose={() => setShowAprovar(false)} title={`Aprovar orçamento nº ${o.numero_orcamento}`}>
          <p className="text-sm text-zinc-600 mb-3">
            Ao aprovar, o orçamento passa a valer como um pedido. A BBH será notificada por e-mail.
          </p>
          <SignaturePad
            onSave={aprovar}
            onCancel={() => setShowAprovar(false)}
            saving={enviando}
          />
        </Modal>
      )}

      {/* Modal recusar (cliente) */}
      {showRecusar && (
        <Modal onClose={() => setShowRecusar(false)} title={`Recusar orçamento nº ${o.numero_orcamento}`}>
          <p className="text-sm text-zinc-600 mb-1">
            Deseja informar o motivo da recusa? <span className="text-zinc-400">(opcional)</span>
          </p>
          <p className="text-xs text-zinc-400 mb-3">
            Sua resposta ajuda a BBH a ajustar propostas futuras.
          </p>
          <textarea
            rows={4}
            value={motivoRecusa}
            onChange={(e) => setMotivoRecusa(e.target.value)}
            data-testid="motivo-recusa"
            placeholder="Ex.: Fora do orçamento, adiar, etc."
            className="w-full px-3 py-2 rounded-md border border-zinc-200 bbh-input text-sm mb-3"
          />
          <div className="flex gap-2">
            <button onClick={() => setShowRecusar(false)} className="flex-1 py-3 rounded-md border border-zinc-200 font-medium">
              Cancelar
            </button>
            <button
              onClick={recusar}
              disabled={enviando}
              data-testid="confirmar-recusa-btn"
              className="flex-1 py-3 rounded-md bg-rose-600 hover:bg-rose-700 text-white font-semibold disabled:opacity-60 inline-flex items-center justify-center gap-2"
            >
              <ThumbsDown className="w-4 h-4" /> {enviando ? "Enviando..." : "Confirmar recusa"}
            </button>
          </div>
        </Modal>
      )}

      {/* Modal reabertura (cliente) */}
      {showReabrir && (
        <Modal onClose={() => setShowReabrir(false)} title="Solicitar reabertura">
          <p className="text-sm text-zinc-600 mb-1">
            Informe o motivo da reabertura/reavaliação do orçamento nº <b>{o.numero_orcamento}</b>:
          </p>
          <textarea
            rows={4}
            value={motivoReab}
            onChange={(e) => setMotivoReab(e.target.value)}
            data-testid="motivo-reabertura"
            placeholder="Ex.: Gostaria de reavaliar os valores..."
            className="w-full px-3 py-2 rounded-md border border-zinc-200 bbh-input text-sm mb-3"
          />
          <div className="flex gap-2">
            <button onClick={() => setShowReabrir(false)} className="flex-1 py-3 rounded-md border border-zinc-200 font-medium">
              Cancelar
            </button>
            <button
              onClick={solicitarReabertura}
              disabled={enviando}
              data-testid="confirmar-reabertura-btn"
              className="flex-1 py-3 rounded-md bg-amber-500 hover:bg-amber-600 text-white font-semibold disabled:opacity-60 inline-flex items-center justify-center gap-2"
            >
              <RotateCcw className="w-4 h-4" /> Enviar solicitação
            </button>
          </div>
        </Modal>
      )}

      {/* Modal admin: enviar link ao cliente */}
      {showEnviarCliente && (
        <Modal onClose={() => setShowEnviarCliente(false)} title="Enviar orçamento ao cliente">
          <p className="text-sm text-zinc-600 mb-2">
            Enviar o link deste orçamento por WhatsApp para{" "}
            <b>{cliente?.nome}</b>
            {cliente?.whatsapp || cliente?.telefone ? (
              <> ({cliente.whatsapp || cliente.telefone})</>
            ) : null}
            .
          </p>
          <p className="text-xs text-zinc-400 mb-3">
            Uma mensagem pronta será aberta no WhatsApp com o link exclusivo do orçamento.
          </p>
          <div className="flex gap-2">
            <button onClick={() => setShowEnviarCliente(false)} className="flex-1 py-3 rounded-md border border-zinc-200 font-medium">
              Cancelar
            </button>
            <button
              onClick={enviarParaCliente}
              data-testid="confirmar-enviar-cliente-btn"
              className="flex-1 py-3 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white font-semibold inline-flex items-center justify-center gap-2"
            >
              <Send className="w-4 h-4" /> Abrir WhatsApp
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function Cond({ k, v }) {
  return (
    <div className="flex justify-between gap-4 border-b border-zinc-100 py-1.5 last:border-0">
      <dt className="text-zinc-500">{k}</dt>
      <dd className="font-medium text-right">{v || "-"}</dd>
    </div>
  );
}

function Modal({ children, title, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white rounded-t-2xl sm:rounded-xl w-full sm:max-w-md p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading text-lg font-semibold">{title}</h3>
          <button onClick={onClose} aria-label="Fechar"><span className="text-zinc-500">✕</span></button>
        </div>
        {children}
      </div>
    </div>
  );
}
